import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import {KanbanCardSkeleton} from './Skeletons';
import useProjectPresence from '../hooks/useProjectPresence';
import {EditingNowStrip} from './presence/Presence';
import './SessionKanbanBoard.css';

/**
 * A running card's live strip: who and which AI agent is editing in its project right now (utils/presenceClient.js).
 * Only on the Active column, whose projects' sandboxes are awake anyway: the board never wakes one to show this.
 */
function CardEditingNow({projectId}) {
  const presence = useProjectPresence(projectId);
  return <EditingNowStrip snapshot={presence} />;
}

CardEditingNow.propTypes = {
  projectId: PropTypes.string.isRequired,
};

const formatDate = (epochSeconds) => {
  if (!epochSeconds) return '';
  const date = new Date(epochSeconds * 1000);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/**
 * Which column a session belongs in. Deliberately backed only by state this
 * app already tracks for a session (no new status concept invented):
 * - Active: has an in-flight /run or /run_sse right now (adk-be-app's
 *   active_runs registry, same source BackgroundTasksIndicator.jsx and
 *   Memories.jsx's own live badge use) — takes priority over pinned/archived,
 *   since "someone is actively working on this" is the most useful thing to
 *   surface regardless of where the user last filed it.
 * - Archived / Pinned: the session's own `state.archived`/`state.pinned`
 *   flags (see AdkSessionsService.js's doc comment on why these live in
 *   plain session state rather than a dedicated pin/archive route).
 * - Recent: everything else — not running, not archived, not pinned.
 */
export const columnFor = (session, activeIds) => {
  if (activeIds.has(session.id)) return 'active';
  if (session.state?.archived) return 'archived';
  if (session.state?.pinned) return 'pinned';
  return 'recent';
};

export const KANBAN_COLUMNS = [
  {key: 'active', label: 'Active', icon: 'zap', hint: 'Currently running', emptyHint: 'Nothing running right now'},
  {key: 'recent', label: 'Recent', icon: 'clock', hint: 'Not running, not filed', emptyHint: 'Conversations will show up here'},
  {key: 'pinned', label: 'Pinned', icon: 'star', hint: 'Kept on top', emptyHint: 'Pin a conversation to keep it here'},
  {key: 'archived', label: 'Archived', icon: 'package', hint: 'Put away', emptyHint: 'Archive a conversation to put it away'},
];

// Skeleton cards per column while loading (name-line widths, varied so it
// doesn't read as a grid of identical blocks).
const SKELETON_NAME_WIDTHS = {
  active: ['72%', '58%'],
  recent: ['80%', '64%', '70%'],
  pinned: ['66%', '54%'],
  archived: ['60%', '74%'],
};

// Per-column bulk actions available when 1+ card is selected in that
// column — `run` receives the array of selected session objects, the
// board's own `onMove`/`onStop`/`onBulkDelete` props, and the selected
// selectable `extraCards` (projects): a move moves those with their own
// `onMove`, a delete hands them to `onBulkDelete` next to the chat ids. Kept
// as plain data (not JSX) so the render loop below stays simple.
const moveAll = (target) => (sessions, {onMove}, extras) => {
  sessions.forEach((s) => onMove(s, target));
  extras.forEach((extra) => extra.onMove?.(target));
};

const BULK_ACTIONS_BY_COLUMN = {
  active: [
    {key: 'stop', label: 'Stop', icon: 'x', destructive: false,
      run: (sessions, {onStop}) => sessions.forEach((s) => onStop(s))},
  ],
  recent: [
    {key: 'pin', label: 'Pin', icon: 'star', destructive: false, run: moveAll('pinned')},
    {key: 'archive', label: 'Archive', icon: 'package', destructive: false, run: moveAll('archived')},
  ],
  pinned: [
    {key: 'archive', label: 'Archive', icon: 'package', destructive: false, run: moveAll('archived')},
    {key: 'recent', label: 'Move to Recent', icon: 'x', destructive: false, run: moveAll('recent')},
  ],
  archived: [
    {key: 'recent', label: 'Move to Recent', icon: 'x', destructive: false, run: moveAll('recent')},
    {key: 'delete', label: 'Delete', icon: 'trash', destructive: true,
      run: (sessions, {onBulkDelete}, extras) => onBulkDelete(sessions.map((s) => s.id), extras)},
  ],
};

// Selection keys of extra cards live in the same per-column Set as the chat
// session ids, namespaced so a card key can never collide with a session id.
const extraSelectionKey = (extra) => `extra:${extra.key}`;

/**
 * Board rendering for the History page's list/kanban view switch
 * (pages/Memories.jsx) — kept as its own component (not inlined there)
 * since it used to also back a standalone Kanban page, and to keep
 * Memories.jsx's already-large render focused on the list view. Callers
 * own data fetching/polling and the actual network calls (`onMove`/
 * `onStop`/`onBulkDelete`); a card click doesn't navigate directly — it
 * calls `onCardClick`, which Memories.jsx wires to open a step-detail
 * drawer (SessionStepsDrawer) rather than leaving the page immediately.
 *
 * Drag-and-drop between columns (native HTML5 DnD, no new dependency —
 * a Kanban board only ever needs "pick this card up, drop it on that
 * column", never in-column reordering) applies one small, uniform rule
 * regardless of which column a card is dropped on:
 *   - Dragged OUT of Active -> `onStop` (this session shouldn't keep
 *     running), THEN `onMove` to wherever it was actually dropped (Recent/
 *     Pinned/Archived) — both fire; stopping doesn't by itself imply any
 *     particular pin/archive state, so the drop target still decides that.
 *   - Dragged from any other column -> just `onMove` to the target column
 *     (covers Recent->Pinned, Recent->Archived, Pinned->Archived, and the
 *     reverse "move back to Recent" already exposed as a per-card button).
 *   - Dropping on Active, or back onto the same column, is a no-op — a
 *     session can't be manually made "active" (that reflects a real
 *     running process, not a user-settable flag).
 *
 * `extraCards` (optional, `{ [columnKey]: [{ key, node }] }`) adds cards that
 * are not chat sessions on top of a column — the sandbox-first Activity puts
 * the sessions of the person's AI tools there (components/sandbox/
 * ToolSessionCard). They count in the column; one with an `onMove(targetColumn)`
 * can be dragged to Recent, Pinned or Archived. One marked `selectable` (with a
 * `label` for its checkbox) takes part in the column's selection like a chat
 * card: select-all includes it, the move actions call its `onMove`, and the
 * Archived column's Delete passes it to `onBulkDelete(sessionIds, extras)`.
 *
 * `loading` (first load still in flight): each column shows card skeletons
 * and a neutral "–" counter instead of its cards, "0" and its empty hint, so
 * nothing claims "Nothing running right now" before the data is in.
 */
function SessionKanbanBoard({sessions, activeIds, movingId, onCardClick, onMove, onStop, onBulkDelete, parseSessionId, extraCards, loading = false, cardNotes = null}) {
  const byColumn = KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col.key] = [];
    return acc;
  }, {});
  sessions.forEach((session) => {
    byColumn[columnFor(session, activeIds)].push(session);
  });

  // { session, sourceColumn } | null — native DnD's dataTransfer can't
  // reliably carry a full object across the drag, so the actual payload
  // travels in this ref instead; dataTransfer only gets a plain id string,
  // set for browser-compatibility (some browsers refuse to start a drag at
  // all with an empty dataTransfer).
  const draggingRef = useRef(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Selection mode is per-column (a card only ever belongs to one column at
  // a time, and the bulk actions offered are column-specific) — keyed by
  // column key, each value a Set of selected session ids in that column.
  const [selectedByColumn, setSelectedByColumn] = useState({});

  const selectedIdsFor = (columnKey) => selectedByColumn[columnKey] || new Set();

  const toggleSelectionMode = (columnKey) => {
    setSelectedByColumn((prev) => {
      const next = {...prev};
      if (columnKey in prev) {
        delete next[columnKey];
      } else {
        next[columnKey] = new Set();
      }
      return next;
    });
  };

  const toggleCardSelected = (columnKey, sessionId) => {
    setSelectedByColumn((prev) => {
      const current = new Set(prev[columnKey] || []);
      if (current.has(sessionId)) current.delete(sessionId);
      else current.add(sessionId);
      return {...prev, [columnKey]: current};
    });
  };

  // Header checkbox in the bulk bar — mirrors the List view's own
  // "select all visible" checkbox (Memories.jsx's handleSelectAllVisible):
  // already-all-selected unchecks everything, otherwise selects every card
  // currently in this column.
  // Every card currently in the column counts, chats and selectable extra
  // cards (projects) alike.
  const toggleSelectAll = (columnKey, selectableKeys) => {
    setSelectedByColumn((prev) => {
      const current = prev[columnKey] || new Set();
      const allSelected = selectableKeys.length > 0 && selectableKeys.every((key) => current.has(key));
      return {
        ...prev,
        [columnKey]: allSelected ? new Set() : new Set(selectableKeys),
      };
    });
  };

  const runBulkAction = (columnKey, action, columnSessions, selectableExtras) => {
    const ids = selectedIdsFor(columnKey);
    const targets = columnSessions.filter((s) => ids.has(s.id));
    const extraTargets = selectableExtras.filter((extra) => ids.has(extraSelectionKey(extra)));
    if (targets.length === 0 && extraTargets.length === 0) return;
    action.run(targets, {onMove, onStop, onBulkDelete}, extraTargets);
    // Exit selection mode for this column once the action is dispatched —
    // the targets are about to leave it (or, for "Stop", the column's own
    // membership will update on the next activeIds poll).
    setSelectedByColumn((prev) => {
      const next = {...prev};
      delete next[columnKey];
      return next;
    });
  };

  const handleDragStart = (session, sourceColumn) => (e) => {
    draggingRef.current = {session, sourceColumn};
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', session.id);
    } catch {
      // Some older/embedded webviews throw on setData with certain MIME
      // types — the real payload lives in draggingRef regardless.
    }
  };

  const handleExtraDragStart = (extra, sourceColumn) => (e) => {
    draggingRef.current = {extra, sourceColumn};
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', extra.key);
    } catch {
      // As above: the payload lives in draggingRef.
    }
  };

  const handleDragEnd = () => {
    draggingRef.current = null;
    setDragOverColumn(null);
  };

  const handleColumnDragOver = (columnKey) => (e) => {
    if (!draggingRef.current || columnKey === 'active') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== columnKey) setDragOverColumn(columnKey);
  };

  const handleColumnDragLeave = (columnKey) => () => {
    setDragOverColumn((prev) => (prev === columnKey ? null : prev));
  };

  const handleColumnDrop = (targetColumn) => (e) => {
    e.preventDefault();
    setDragOverColumn(null);
    const dragging = draggingRef.current;
    draggingRef.current = null;
    if (!dragging || targetColumn === 'active') return;
    const {session, extra, sourceColumn} = dragging;
    if (sourceColumn === targetColumn) return;
    if (extra) {
      extra.onMove(targetColumn);
      return;
    }
    if (sourceColumn === 'active') onStop(session);
    onMove(session, targetColumn);
  };

  return (
    <div className="session-kanban-board">
      {KANBAN_COLUMNS.map((col) => {
        const columnSessions = byColumn[col.key];
        const selectionMode = col.key in selectedByColumn;
        const selectedIds = selectedIdsFor(col.key);
        const bulkActions = BULK_ACTIONS_BY_COLUMN[col.key] || [];
        const columnExtras = extraCards?.[col.key] || [];
        const selectableExtras = columnExtras.filter((extra) => extra.selectable);
        const selectableKeys = [...columnSessions.map((s) => s.id), ...selectableExtras.map(extraSelectionKey)];
        const allSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedIds.has(key));

        return (
          <div
            className={`session-kanban-column${dragOverColumn === col.key ? ' is-drag-over' : ''}`}
            key={col.key}
            onDragLeave={handleColumnDragLeave(col.key)}
            onDragOver={handleColumnDragOver(col.key)}
            onDrop={handleColumnDrop(col.key)}
          >
            <div className="session-kanban-column-header">
              <Icon name={col.icon} size={15} />
              <span className="session-kanban-column-label">{col.label}</span>
              {loading ? (
                <span aria-label="Loading" className="session-kanban-column-count is-loading">{'–'}</span>
              ) : (
                <span className="session-kanban-column-count">{columnSessions.length + columnExtras.length}</span>
              )}
              {!loading && selectableKeys.length > 0 && (
                <button
                  className={`session-kanban-select-toggle${selectionMode ? ' active' : ''}`}
                  onClick={() => toggleSelectionMode(col.key)}
                  title={selectionMode ? 'Cancel selection' : 'Select multiple'}
                  type="button"
                >
                  {selectionMode ? 'Cancel' : 'Select'}
                </button>
              )}
            </div>
            <div className="session-kanban-column-hint">{col.hint}</div>

            {selectionMode && (
              <div className="session-kanban-bulk-bar">
                <label className="session-kanban-bulk-select-all">
                  <input
                    aria-label={`Select all in ${col.label}`}
                    checked={allSelected}
                    onChange={() => toggleSelectAll(col.key, selectableKeys)}
                    type="checkbox"
                  />
                  <span className="session-kanban-bulk-count">
                    {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
                  </span>
                </label>
                <div className="session-kanban-bulk-actions">
                  {bulkActions.map((action) => (
                    <button
                      aria-label={action.label}
                      className={`session-kanban-bulk-action-btn${action.destructive ? ' destructive' : ''}`}
                      disabled={selectedIds.size === 0}
                      key={action.key}
                      onClick={() => runBulkAction(col.key, action, columnSessions, selectableExtras)}
                      title={action.label}
                      type="button"
                    >
                      <Icon name={action.icon} size={13} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div aria-busy={loading || undefined} className="session-kanban-column-cards">
              {loading && SKELETON_NAME_WIDTHS[col.key].map((width, i) => (
                <KanbanCardSkeleton key={i} nameWidth={width} />
              ))}
              {!loading && columnSessions.length === 0 && columnExtras.length === 0 && (
                <div className="session-kanban-empty">{col.emptyHint || 'Nothing here'}</div>
              )}
              {!loading && columnExtras.map((extra) => (selectionMode && extra.selectable ? (
                // In selection mode a selectable extra card toggles on click, like a
                // chat card; its own buttons (e.g. "See the changes") are switched
                // off meanwhile (CSS), the checkbox carries the keyboard path.
                <div
                  className={`session-kanban-extra-selectable${selectedIds.has(extraSelectionKey(extra)) ? ' is-selected' : ''}`}
                  key={extra.key}
                  onClick={() => toggleCardSelected(col.key, extraSelectionKey(extra))}
                >
                  <label className="session-kanban-card-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      aria-label={`Select ${extra.label || extra.key}`}
                      checked={selectedIds.has(extraSelectionKey(extra))}
                      onChange={() => toggleCardSelected(col.key, extraSelectionKey(extra))}
                      type="checkbox"
                    />
                  </label>
                  <div className="session-kanban-extra-selectable-card" inert>{extra.node}</div>
                </div>
              ) : extra.onMove ? (
                <div
                  className="session-kanban-extra-draggable"
                  draggable
                  key={extra.key}
                  onDragEnd={handleDragEnd}
                  onDragStart={handleExtraDragStart(extra, col.key)}
                >
                  {extra.node}
                </div>
              ) : <React.Fragment key={extra.key}>{extra.node}</React.Fragment>))}
              {!loading && columnSessions.map((session) => {
                const parsed = parseSessionId ? parseSessionId(session.id) : null;
                const isSelected = selectedIds.has(session.id);
                return (
                  <div
                    className={`session-kanban-card${isSelected ? ' is-selected' : ''}${selectionMode ? ' has-checkbox' : ''}`}
                    draggable={!selectionMode}
                    key={session.id}
                    onClick={() => (selectionMode ? toggleCardSelected(col.key, session.id) : onCardClick(session))}
                    onDragEnd={handleDragEnd}
                    onDragStart={handleDragStart(session, col.key)}
                  >
                    {selectionMode && (
                      <label className="session-kanban-card-checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          aria-label={`Select ${session.name || 'Untitled Conversation'}`}
                          checked={isSelected}
                          onChange={() => toggleCardSelected(col.key, session.id)}
                          type="checkbox"
                        />
                      </label>
                    )}
                    <div className="session-kanban-card-name">{session.name || 'Untitled Conversation'}</div>
                    {parsed?.projectId && (
                      <div className="session-kanban-card-project">
                        <Icon name="folder" size={11} />
                        {parsed.projectId}
                      </div>
                    )}
                    {col.key === 'active' && parsed?.projectId && <CardEditingNow projectId={parsed.projectId} />}
                    {/* A note under a card, e.g. the drift warning: others changed its files since (hooks/useSessionDrift). */}
                    {cardNotes?.[session.id] || null}
                    <div className="session-kanban-card-footer">
                      <span className="session-kanban-card-time">{formatDate(session.lastUpdateTime)}</span>
                      {!selectionMode && (
                        <div className="session-kanban-card-actions" onClick={(e) => e.stopPropagation()}>
                          {col.key === 'active' && (
                            <button
                              className="session-kanban-card-action-btn"
                              disabled={movingId === session.id}
                              onClick={() => onStop(session)}
                              title="Stop"
                              type="button"
                            >
                              <Icon name="x" size={12} />
                            </button>
                          )}
                          {col.key !== 'pinned' && col.key !== 'active' && (
                            <button
                              className="session-kanban-card-action-btn"
                              disabled={movingId === session.id}
                              onClick={() => onMove(session, 'pinned')}
                              title="Pin"
                              type="button"
                            >
                              <Icon name="star" size={12} />
                            </button>
                          )}
                          {col.key !== 'archived' && col.key !== 'active' && (
                            <button
                              className="session-kanban-card-action-btn"
                              disabled={movingId === session.id}
                              onClick={() => onMove(session, 'archived')}
                              title="Archive"
                              type="button"
                            >
                              <Icon name="package" size={12} />
                            </button>
                          )}
                          {(col.key === 'pinned' || col.key === 'archived') && (
                            <button
                              className="session-kanban-card-action-btn"
                              disabled={movingId === session.id}
                              onClick={() => onMove(session, 'recent')}
                              title="Move to Recent"
                              type="button"
                            >
                              <Icon name="x" size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

SessionKanbanBoard.propTypes = {
  activeIds: PropTypes.instanceOf(Set).isRequired,
  movingId: PropTypes.string,
  onBulkDelete: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
  parseSessionId: PropTypes.func,
  extraCards: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    node: PropTypes.node,
    onMove: PropTypes.func,
    selectable: PropTypes.bool,
    label: PropTypes.string,
    item: PropTypes.object,
  }))),
  sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  cardNotes: PropTypes.objectOf(PropTypes.node),
};

SessionKanbanBoard.defaultProps = {
  movingId: null,
  parseSessionId: null,
  extraCards: null,
  loading: false,
  cardNotes: null,
};

export default SessionKanbanBoard;
